import { useMemo } from "react";

import { useAsyncQuery } from "@/ds";
import type { FilterDefinition } from "@/ds";
import { listUserTimes } from "@/modules/users";
import type { UserFilterOption } from "@/modules/users";
import type { UseRoleOptionsResult } from "@/screens/control-panel/hooks/use-role-options";
import {
  PERFIL_FILTER_KEY,
  PERFIL_FILTER_LABEL,
  TIME_FILTER_KEY,
  TIME_FILTER_LABEL,
  USERS_ERROR_MESSAGE,
  USERS_FORBIDDEN_MESSAGE,
} from "../users.constants";

const NO_OPTIONS: UserFilterOption[] = [];

/**
 * Filtros inline (Figma 4434:111162), ambos de multisseleção: Time com o catálogo completo do servidor (e não só os times
 * da página em tela) e Perfil com os perfis já carregados para o modal de edição. Cada campo carrega e falha por conta própria.
 */
export function useUsersFiltrosColunas(roles: UseRoleOptionsResult): FilterDefinition[] {
  const times = useAsyncQuery({
    fetcher: (signal) => listUserTimes(signal),
    queryKey: TIME_FILTER_KEY,
    initialData: NO_OPTIONS,
    forbiddenMessage: USERS_FORBIDDEN_MESSAGE,
    errorMessage: USERS_ERROR_MESSAGE,
  });

  return useMemo(
    () => [
      {
        key: TIME_FILTER_KEY,
        label: TIME_FILTER_LABEL,
        type: "multiselect" as const,
        options: times.data,
        isLoading: times.status === "loading",
        hasError: times.status === "error" || times.status === "forbidden",
        searchPlaceholder: "Buscar time",
      },
      {
        key: PERFIL_FILTER_KEY,
        label: PERFIL_FILTER_LABEL,
        type: "multiselect" as const,
        options: roles.options,
        isLoading: roles.isLoading,
        hasError: roles.hasError,
        searchPlaceholder: "Buscar perfil",
      },
    ],
    [times.data, times.status, roles.options, roles.isLoading, roles.hasError],
  );
}
